import invariant from 'invariant';
import { isEmpty } from 'lodash';
import React from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage, intlShape } from 'react-intl';

import * as assetSelectors from 'state/asset/selectors/assetSelectors';
import { getInfoStatuses } from 'state/buildStatus/selectors/buildStatusSelectors';
import * as editorSelectors from 'state/editor/selectors/editorSelectors';
import * as manageSubtitlesActions from 'state/subtitles/actions/manageSubtitlesActions';
import * as subtitlesActions from 'state/subtitles/actions/subtitlesActions';
import * as subtitlesSelectors from 'state/subtitles/selectors/subtitlesSelectors';

import { UploadPurpose } from 'config/constants';
import { pencil, save } from 'icons/SDS/allIcons';
import { intlConnect } from 'utils/connectUtils';
import * as grapheneUtils from 'utils/grapheneUtils';
import { getMessageFromId, registerIntlMessage } from 'utils/intlMessages/intlMessages';

import AlertBox, { AlertType } from 'views/common/components/AlertBox/AlertBox';
import SDSButton, { ButtonType } from 'views/common/components/SDSButton/SDSButton';
import SDSTooltip, { TooltipPosition } from 'views/common/components/SDSTooltip/SDSTooltip';
import type { DisplayRange } from 'views/common/components/SubtitlesPreview/components/SubtitlesBody/SubtitlesBody';
import SubtitlesBody from 'views/common/components/SubtitlesPreview/components/SubtitlesBody/SubtitlesBody';
import { getEmptySubtitleFragment } from 'views/subtitles/NewSubtitlesButton/NewSubtitlesButton';
import { updateSubtitlesState } from 'views/subtitles/state/actions/subtitlesEditorActions';
import { getSubtitlesEditorState } from 'views/subtitles/state/selectors/subtitlesEditorSelectors';
import type { SubtitlesEditorState } from 'views/subtitles/state/types/subtitles';

import style from './SubtitlesPreview.scss';
import {
  parseSubtitles,
  SubtitlesValidationError,
  SubtitlesValidationErrorType,
  validateFragments,
} from './subtitleParser';

import type { AssetID, VideoAsset } from 'types/assets';
import { InfoStatus } from 'types/build';
import type { SnapId } from 'types/common';
import { ExtractDispatchProps } from 'types/redux';
import type { State as ReduxState } from 'types/rootState';

registerIntlMessage({
  intlMessage: (
    <FormattedMessage
      id="subtitles-track-title"
      description="Title used for subtitles track in certain language"
      defaultMessage="{language} subtitle track"
    />
  ),
  params: ['language'],
});

type ErrorCreator = (argument: string | undefined) => ReactNode;

const ValidationErrorToMessage: { [key in SubtitlesValidationErrorType]: ErrorCreator } = {
  [SubtitlesValidationErrorType.TIMESTAMP_ERROR]: invalidTimings => (
    <FormattedMessage
      key="subtitles-error-timestamp"
      id="subtitles-error-timestamp-with-timings"
      description="Error displayed when the subtitles timestamp endTime is before startTime"
      defaultMessage="One of the subtitle timings is incorrect. The end time must be higher than start time: {invalidTimings}"
      values={{ invalidTimings }}
    />
  ),
};

type UploadParams = {
  purpose: UploadPurpose;
  componentId?: string; // Optional. ComponentId is valid only for top snap and longforms,
  snapId?: SnapId; // Optional. SnapId is valid only for top snap and longforms
};

type ExternalProps = {
  uploadParams: UploadParams;
  videoAssetId: AssetID;
  isWaitingForSubtitles: boolean;
  topsnapId: SnapId;
  displayRange: DisplayRange | undefined | null;
};

export function mapStateToProps(state: ReduxState, props: ExternalProps) {
  const editionId = editorSelectors.getActiveEditionId(state);

  const videoAsset: VideoAsset | undefined | null = assetSelectors.getAssetById(state)(props.videoAssetId) as any;

  const subtitlesTrack = subtitlesSelectors.getSubtitlesByActiveLanguageAndVideoAssetId(state)(props.videoAssetId);
  const rawSubtitles = subtitlesTrack
    ? subtitlesSelectors.getSubtitlesTrackContentById(state)(subtitlesTrack.assetId)
    : undefined;
  const subtitlesAssetId = subtitlesTrack && subtitlesTrack.assetId;
  const subtitlesEditorState = subtitlesAssetId ? getSubtitlesEditorState(state)(subtitlesAssetId) : null;
  const isReadOnly = editorSelectors.isReadOnly(state);
  const isLoadingSubtitlesTrack = subtitlesAssetId
    ? subtitlesSelectors.getSubtitlesTrackLoadingById(state)(subtitlesAssetId)
    : false;

  return {
    editionId,
    isReadOnly,
    rawSubtitles,
    subtitlesTrack,
    videoAsset,
    videoDuration: (videoAsset && videoAsset.durationMillis) || 0,
    subtitlesEditorState,
    isLoadingSubtitlesTrack,
    infoStatuses: editionId ? getInfoStatuses(state)(editionId) : [],
  };
}

const mapDispatchToProps = {
  // There was a circular dependency when I've added save action to subtitles actions
  saveSubtitles: manageSubtitlesActions.saveSubtitles,
  loadSubtitles: subtitlesActions.loadSubtitles,
  updateSubtitlesState,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ExtractDispatchProps<typeof mapDispatchToProps>;
type Props = ExternalProps & StateProps & DispatchProps;

export class SubtitlesPreview extends React.PureComponent<Props> {
  static contextTypes = {
    intl: intlShape,
  };

  componentDidMount() {
    this.props.loadSubtitles(this.props.videoAssetId);
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.isWaitingForSubtitles && !this.props.isWaitingForSubtitles) {
      this.props.loadSubtitles(this.props.videoAssetId);
    }
  }

  toggleEditing = () => {
    const { displayRange, subtitlesEditorState } = this.props;

    let newSubtitlesEditorState: Partial<SubtitlesEditorState> = {
      isEditing: (prevIsEditing: boolean) => !prevIsEditing,
    };
    if (!this.getIsEditing()) {
      grapheneUtils.incrementCounter('subtitles', { click: 'edit' });
      if (isEmpty(subtitlesEditorState?.subtitleFragments)) {
        grapheneUtils.incrementCounter('subtitles', { click: 'add_fragment' });
        newSubtitlesEditorState = {
          ...newSubtitlesEditorState,
          subtitleFragments: [getEmptySubtitleFragment(displayRange)],
        };
      }
    }
    this.updateSubtitlesState(newSubtitlesEditorState);
  };

  getValidationErrors(): SubtitlesValidationError[] {
    const subtitleFragments = this.props.subtitlesEditorState?.subtitleFragments;

    if (!subtitleFragments) {
      return [];
    }

    return validateFragments(subtitleFragments);
  }

  handleSubtitlesSave = () => {
    this.toggleEditing();
    const { subtitlesTrack } = this.props;
    invariant(subtitlesTrack, 'Subtitle track must be defined');
    const subtitlesAssetId = subtitlesTrack && subtitlesTrack.assetId;

    this.props.saveSubtitles(subtitlesAssetId, {
      assetId: this.props.videoAssetId,
      editionId: this.props.editionId,
      subtitlesLanguage: subtitlesTrack.language,
      ...this.props.uploadParams,
    });

    grapheneUtils.incrementCounter('subtitles', { click: 'save', language: subtitlesTrack.language });
  };

  getIsEditing() {
    const { subtitlesEditorState } = this.props;
    return (subtitlesEditorState && subtitlesEditorState.isEditing) || false;
  }

  updateSubtitlesState(subtitlesEditorState: Partial<SubtitlesEditorState>) {
    const { subtitlesTrack } = this.props;
    if (!subtitlesTrack?.assetId) {
      return;
    }

    this.props.updateSubtitlesState(subtitlesTrack.assetId, subtitlesEditorState);
  }

  handleCancelEditing = () => {
    const { rawSubtitles } = this.props;

    invariant(rawSubtitles, 'Raw subtitles must be defined on discard');

    this.updateSubtitlesState({
      subtitleFragments: parseSubtitles(rawSubtitles),
      isEditing: false,
    });

    grapheneUtils.incrementCounter('subtitles', { click: 'cancel' });
  };

  renderSubtitles = () => {
    const { subtitlesTrack, videoDuration, displayRange } = this.props;

    return (
      <SubtitlesBody
        key={subtitlesTrack ? subtitlesTrack.source : undefined}
        displayRange={displayRange}
        isEditing={this.getIsEditing()}
        subtitlesTrack={subtitlesTrack}
        videoDuration={videoDuration}
      />
    );
  };

  renderSubtitleEditButton() {
    return (
      <SDSButton
        type={ButtonType.WHITE}
        inlineIcon={pencil}
        data-test="common.subtitlesPreview.edit.button"
        onClick={this.toggleEditing}
        disabled={this.props.isReadOnly || this.props.isWaitingForSubtitles || this.props.isLoadingSubtitlesTrack}
        block
      >
        {getMessageFromId('edit-button-label')}
      </SDSButton>
    );
  }

  renderSubtitleControls() {
    if (this.getIsEditing()) {
      const validationErrors = this.getValidationErrors();
      return (
        <div className={style.buttonGroup}>
          {this.wrapWithValidationErrorsTooltip(validationErrors)(
            <SDSButton
              inlineIcon={save}
              onClick={this.handleSubtitlesSave}
              disabled={validationErrors.length > 0}
              type={ButtonType.WHITE}
              data-test="subtitlesPreview.save"
              block
            >
              {getMessageFromId('save-button-text')}
            </SDSButton>
          )}
          <div className={style.splitter} />
          <SDSButton onClick={this.handleCancelEditing} type={ButtonType.WHITE} data-test="subtitlesPreview.cancel">
            {getMessageFromId('cancel-button-modal')}
          </SDSButton>
        </div>
      );
    }

    return (
      <div className={style.buttonGroup} data-test="subtitlesPreview.editControl">
        {this.renderSubtitleEditButton()}
      </div>
    );
  }

  wrapWithValidationErrorsTooltip = (validationErrors: SubtitlesValidationError[]) => (
    childElement: ReactNode
  ): ReactNode => {
    if (validationErrors.length === 0) {
      return childElement;
    }
    const tooltipText = validationErrors.map(error => ValidationErrorToMessage[error.type](error.argument));
    return (
      <SDSTooltip
        className={style.subtitleErrorTooltip}
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        placement={TooltipPosition.TOP}
        id="subtitles-error"
        title={tooltipText}
        data-test="subtitles.error.tooltip"
      >
        {childElement}
      </SDSTooltip>
    );
  };

  renderAutoGeneratedInfoBox() {
    if (!this.props.infoStatuses.includes(InfoStatus.AUTO_GENENERATED_SUBS)) {
      return null;
    }

    return (
      <AlertBox type={AlertType.INFO}>
        <FormattedMessage
          id="review-subs-time-spent"
          description="Text telling the user that the subtitles were auto generated and they need to review them"
          defaultMessage="Review and improve auto-generated subtitles to improve time-spent"
          data-test="SubtitlesPreview.review-auto-gen-subs"
        />
      </AlertBox>
    );
  }

  renderHeader() {
    return (
      <div className={style.subtitlesControlBlock} key="SubtitlesPreview-subtitleTitleBlock">
        <span className={style.subtitlesTitle} />
        {this.renderSubtitleControls()}
      </div>
    );
  }

  render() {
    const { videoAsset, subtitlesTrack, isLoadingSubtitlesTrack } = this.props;

    if (videoAsset && !subtitlesTrack && !isLoadingSubtitlesTrack) {
      return (
        <div className={style.message}>
          <FormattedMessage
            id="subtitles-empty-track-text"
            description="Text telling the user that the current subtitle track is empty"
            defaultMessage="This subtitle track is empty. Upload or generate subtitles above."
            data-test="SubtitlesPreview.emptyTrackMessage"
          />
        </div>
      );
    }

    return (
      <div data-test="subtitlesPreview.subtitleBlock" className={style.subtitleBlock}>
        {this.renderAutoGeneratedInfoBox()}
        {this.renderHeader()}
        {this.renderSubtitles()}
      </div>
    );
  }
}

export default intlConnect(mapStateToProps, mapDispatchToProps)(SubtitlesPreview);
